import { Extension } from "@tiptap/core"
import { type EditorState, Plugin, PluginKey } from "@tiptap/pm/state"
import { Decoration, DecorationSet } from "@tiptap/pm/view"

export type Principle = "grammar" | "tighten" | "refine"
export interface ProofreadDecoration {
  id: string
  from: number
  to: number
  originalText: string
  modifiedText: string
  principle: Principle
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    proofread: {
      setProofreadDecorations: (
        decorations: ProofreadDecoration[],
      ) => ReturnType
      clearProofreadDecorations: () => ReturnType
      removeProofreadDecoration: (id: string) => ReturnType
      acceptProofreadChange: (decoration: ProofreadDecoration) => ReturnType
    }
  }
}

export const proofreadKey = new PluginKey("proofread")
const PROOFREAD_META_KEY = "proofreadDecorations"

interface PluginState {
  decorations: DecorationSet
  storedDecorations: ProofreadDecoration[]
  activeId: string | null
}

function buildDecorations(
  state: EditorState,
  decorations: ProofreadDecoration[],
  activeId: string | null,
) {
  if (!decorations || decorations.length === 0) {
    return DecorationSet.empty
  }

  const decos = decorations.flatMap(deco => {
    const { from, to, principle } = deco
    const isActive = deco.id === activeId
    const isDeletion = deco.modifiedText === ""

    if (from >= 0 && to <= state.doc.content.size && from < to) {
      return Decoration.inline(from, to, {
        class: [
          "proofread-decoration",
          `proofread-${principle}`,
          isActive ? "proofread-active" : "",
          isDeletion ? "proofread-deletion" : "",
        ]
          .filter(Boolean)
          .join(" "),
        "data-proofread-id": deco.id,
        "data-principle": principle,
      })
    }
    return []
  })

  return DecorationSet.create(state.doc, decos)
}

function findActiveDecorationId(
  storedDecorations: ProofreadDecoration[],
  cursorPos: number,
) {
  for (const deco of storedDecorations) {
    if (cursorPos >= deco.from && cursorPos <= deco.to) {
      return deco.id
    }
  }
  return null
}

export const ProofreadExtension = Extension.create({
  name: "proofread",

  addCommands() {
    return {
      setProofreadDecorations:
        decorations =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.setMeta(PROOFREAD_META_KEY, decorations)
            dispatch(tr)
          }
          return true
        },
      clearProofreadDecorations:
        () =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.setMeta(PROOFREAD_META_KEY, [])
            dispatch(tr)
          }
          return true
        },
      removeProofreadDecoration:
        (id: string) =>
        ({ tr, dispatch, state }) => {
          if (dispatch) {
            const pluginState: PluginState | undefined =
              proofreadKey.getState(state)
            if (pluginState) {
              const newDecorations = pluginState.storedDecorations.filter(
                d => d.id !== id,
              )
              tr.setMeta(PROOFREAD_META_KEY, newDecorations)
              dispatch(tr)
            }
          }
          return true
        },
      acceptProofreadChange:
        (decoration: ProofreadDecoration) =>
        ({ tr, dispatch, state, commands }) => {
          const pluginState: PluginState | undefined =
            proofreadKey.getState(state)
          if (!pluginState) return false

          commands.setTextSelection({
            from: decoration.from,
            to: decoration.to,
          })
          commands.insertContent(decoration.modifiedText)

          if (dispatch) {
            const newDecorations = pluginState.storedDecorations
              .filter(d => d.id !== decoration.id)
              .map(d => ({
                ...d,
                from: tr.mapping.map(d.from),
                to: tr.mapping.map(d.to),
              }))
              .filter(d => d.from < d.to)

            tr.setMeta(PROOFREAD_META_KEY, newDecorations)
            dispatch(tr)
          }
          return true
        },
    }
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: proofreadKey,
        state: {
          init: (): PluginState => {
            return {
              decorations: DecorationSet.empty,
              storedDecorations: [],
              activeId: null,
            }
          },
          apply: (tr, value: PluginState, _oldState, newState): PluginState => {
            const metaDecorations = tr.getMeta(PROOFREAD_META_KEY)
            let storedDecorations = value.storedDecorations

            if (metaDecorations !== undefined) {
              storedDecorations = metaDecorations
            } else if (tr.docChanged) {
              storedDecorations = storedDecorations
                .map(d => ({
                  ...d,
                  from: tr.mapping.map(d.from),
                  to: tr.mapping.map(d.to),
                }))
                .filter(d => d.from < d.to)
            }

            const cursorPos = newState.selection.from
            const activeId = findActiveDecorationId(
              storedDecorations,
              cursorPos,
            )

            const decorations = buildDecorations(
              newState,
              storedDecorations,
              activeId,
            )

            return { decorations, storedDecorations, activeId }
          },
        },
        props: {
          decorations: state => {
            const pluginState = proofreadKey.getState(state)
            return pluginState?.decorations || DecorationSet.empty
          },
        },
      }),
    ]
  },
})
